import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Search, Loader2, ChevronRight } from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import PeriodPicker from '../../components/venduto/PeriodPicker';
import { periodoRange, defaultSelezione } from '../../lib/venduto';
import { fmtSoldi, fmtDataIt } from '../../lib/acquisti';

const AcquistiFornitoriPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [q, setQ] = useState('');
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    acquistiApi.getStato().then((s) => {
      setStato(s);
      setSel(defaultSelezione(s));
    }).catch(() => setCaricando(false));
  }, []);

  const range = sel ? periodoRange(sel, stato) : null;

  const carica = async (r) => {
    setCaricando(true);
    try {
      setFornitori(await acquistiApi.getFornitori(r.data_inizio, r.data_fine));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setCaricando(false);
    }
  };

  useEffect(() => {
    if (range) carica(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.data_inizio, range?.data_fine]);

  const classifica = async (id, categoria) => {
    try {
      await acquistiApi.setCategoriaFornitore(id, categoria === NESSUNA ? null : categoria);
      toast.success('Categoria salvata e applicata allo storico');
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    }
  };

  const filtrati = fornitori.filter((f) =>
    !q.trim() || `${f.denominazione} ${f.piva_cf}`.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader titolo="Fornitori" sottotitolo={range?.label || 'Periodo'} />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {sel && <PeriodPicker value={sel} onChange={setSel} archivio={stato} testid="fornitori-periodo" />}

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca fornitore o partita IVA"
            className="h-11 pl-9"
            data-testid="fornitori-search-input"
          />
          {caricando && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>

        <div className="space-y-2" data-testid="fornitori-lista">
          {filtrati.map((f) => (
            <Card key={f.id} data-testid={`fornitore-item-${f.id}`}>
              <CardContent className="p-3 space-y-2">
                <div
                  className="flex items-start justify-between gap-3 cursor-pointer"
                  onClick={() => navigate(`/acquisti/fornitori/${f.id}`)}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{f.denominazione}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.documenti_periodo} fatture nel periodo · ultimo acquisto {fmtDataIt(f.ultimo_acquisto)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Totale storico {fmtSoldi(f.totale_acquistato)} · {f.n_documenti} fatture
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold tabular-nums">{fmtSoldi(f.totale_periodo)}</p>
                    {f.quota_pct !== null && f.totale_periodo > 0 && (
                      <p className="text-[11px] text-muted-foreground">{f.quota_pct}% del periodo</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </div>

                <div className="flex items-center justify-between gap-2 pt-1 border-t">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Categoria</span>
                  {isAdmin ? (
                    <CategoriaSelect
                      valore={f.categoria_default}
                      categorie={stato?.categorie || []}
                      onChange={(v) => classifica(f.id, v)}
                      testid={`categoria-fornitore-${f.id}`}
                      className="h-8 w-[180px]"
                    />
                  ) : (
                    <Badge variant={f.categoria_default ? 'secondary' : 'outline'} className="text-[10px]">
                      {f.categoria_default || 'DA CLASSIFICARE'}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {!caricando && filtrati.length === 0 && (
            <Card data-testid="fornitori-vuoto">
              <CardContent className="p-4 text-sm text-muted-foreground">Nessun fornitore trovato.</CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AcquistiFornitoriPage;
